/**
 * profileOffice.js — who may set a person's office, and what a profile save
 * writes for it. (RW-2989, RW-2957)
 *
 * A person's office decides which office-only feed posts they can read
 * (firestore.rules posts `canReadPostAudience`), so Clint (10-06): "only admins
 * should be able to do that". firestore.rules (users, `selfOfficeWriteOk`)
 * enforces it; this module keeps every profile form inside the rule, so a
 * non-admin's save never carries an office change the rule would refuse —
 * which would fail the WHOLE save, name and phone included.
 *
 *   - An admin may set, change or clear anyone's office.
 *   - A non-admin may set their OWN office once, while `officeId` is missing
 *     or blank (the welcome wizard; My Profile before anyone set it).
 *   - After that it is read-only for them: "Ask an admin to change your office".
 *
 * `feedOffices` (the offices a person's feed may show, RW-2957 Q3) is
 * admin-only outright; `feedOfficesPatch` builds the admin's write.
 */

import { canonicalOfficeKey } from './officeMismatch';
import { OFFICES } from './offices';

const hasOfficeId = (doc) => !!doc && typeof doc.officeId === 'string' && doc.officeId !== '';

/**
 * The office a form starts on: officeId, else the legacy office / primaryOffice
 * resolved to its slug (older docs hold "New Orleans"). Before this, My Profile
 * and Deals › Settings started on the legacy `office` alone — '' on most docs —
 * and a save wrote that blank over officeId.
 */
export function profileOfficeOf(doc) {
  if (!doc) return '';
  if (hasOfficeId(doc)) return doc.officeId;
  return canonicalOfficeKey(doc.office || doc.primaryOffice || '');
}

/**
 * RW-3708 — a Darwin roster office name ("Prytania Street" / "Baton Rouge" /
 * "Northshore") as a known office id, or ''. An unrecognised name resolves to
 * '' rather than to a slug no office filter would ever match.
 */
export function rosterOfficeId(name) {
  const key = canonicalOfficeKey(name || '');
  return OFFICES[key] ? key : '';
}

/**
 * RW-3708 — the office a person belongs to, for every screen that shows or
 * filters by it: the profile's own office (profileOfficeOf: officeId, else the
 * legacy office / primaryOffice), else the office the Darwin roster sync stamps
 * on the user doc (`darwinOffice`, RW-2558). The Leaderboard fell back to the
 * roster; the directory, its chips, the Org Chart and the Offices counts did
 * not — so about a dozen people, the #1 producer among them, had an office on
 * one Company screen and none on the next. Read-only: never write this back as
 * the profile's office (profileOfficeOf stays what the forms start on).
 */
export function personOfficeId(doc) {
  return profileOfficeOf(doc) || rosterOfficeId(doc && doc.darwinOffice);
}

/** RW-3708 — is this person in this office (their own, or an additional one)? */
export function personInOffice(doc, officeId) {
  if (!doc || !officeId) return false;
  return personOfficeId(doc) === officeId || (Array.isArray(doc.additionalOffices) && doc.additionalOffices.includes(officeId));
}

/** May this editor change this profile's office? */
export function canChangeOffice({ isAdmin, doc } = {}) {
  if (isAdmin) return true;
  return !hasOfficeId(doc);
}

/**
 * The office keys a profile save writes: {} when nothing should change (the
 * office is untouched, or locked for this editor), else the triple
 * officeId / office / primaryOffice — always equal, which the rule requires of
 * a first self-set. A non-admin may not clear one.
 */
export function officeWritePatch({ isAdmin, doc, chosen } = {}) {
  const next = typeof chosen === 'string' ? chosen : '';
  const current = hasOfficeId(doc) ? doc.officeId : '';
  // Untouched (the form still shows what it started on): write nothing, so a
  // profile save never re-stamps or "repairs" the office as a side effect.
  if (next === profileOfficeOf(doc)) return {};
  if (!isAdmin) {
    if (current) return {}; // locked — the rule would refuse it
    if (!next) return {};   // nothing chosen; never write a blank
  }
  return { officeId: next, office: next, primaryOffice: next };
}

/** Normalize a feedOffices value from a doc: a list of ids, or null (= the default). */
export function feedOfficesOf(doc) {
  if (!doc || !Array.isArray(doc.feedOffices)) return null;
  return doc.feedOffices.filter((o) => typeof o === 'string' && o !== '');
}

/**
 * The admin's feedOffices write: undefined when unchanged; `null` to clear
 * (an empty selection = "use the default"), else the sorted list. The caller
 * maps null to deleteField().
 */
export function feedOfficesPatch({ doc, chosen } = {}) {
  const before = feedOfficesOf(doc);
  const next = Array.isArray(chosen) ? [...new Set(chosen.filter((o) => typeof o === 'string' && o !== ''))].sort() : [];
  const was = before ? [...before].sort() : [];
  if (next.length === 0) return before === null ? undefined : null;
  if (before !== null && next.join('|') === was.join('|')) return undefined;
  return next;
}
